import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, AppStateStatus } from 'react-native';
import type { AppDispatch } from '../store';
import {
  selectActiveNotifications,
  selectDeliveryHistory,
  selectEngagementPatterns,
  selectNotificationOptimization,
  recordNotificationEngagement,
  fetchEngagementPatterns,
  optimizeNotificationTiming,
} from '../store/slices/notificationIntelligenceSlice';
import {
  notificationEngagementService,
  type NotificationEngagementData,
  type NotificationFeedback,
} from '../services/notificationEngagementService';
import type { SmartNotification } from '@coffeeplexity/shared';
import { logger } from '../utils/logger';

export interface UseNotificationEngagementReturn {
  // Engagement tracking
  recordEngagement: (
    notification: SmartNotification,
    engagementType: 'opened' | 'clicked' | 'dismissed'
  ) => Promise<void>;

  // Feedback collection
  submitFeedback: (
    notificationId: string,
    feedback: Omit<
      NotificationFeedback,
      'notificationId' | 'userId' | 'timestamp'
    >
  ) => Promise<void>;

  // Pattern analysis
  analyzeUserPatterns: () => Promise<void>;
  triggerOptimization: () => Promise<void>;

  // Metrics
  getEngagementMetrics: () => {
    totalNotifications: number;
    engagementRate: number;
    clickThroughRate: number;
    dismissalRate: number;
  };

  // Timing recommendations
  getTimingRecommendations: () => {
    recommendations: string[];
    confidence: number;
    reasoning: string[];
  };

  // State
  isAnalyzing: boolean;
  isOptimizing: boolean;
  lastAnalyzed: string | null;
  error: string | null;
}

export const useNotificationEngagement = (
  userId: string
): UseNotificationEngagementReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [lastAnalyzed, setLastAnalyzed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [appState, setAppState] = useState<AppStateStatus>(
    AppState.currentState
  );

  // Selectors
  const activeNotifications = useSelector(selectActiveNotifications);
  const deliveryHistory = useSelector(selectDeliveryHistory);
  const engagementPatterns = useSelector(selectEngagementPatterns);
  const optimization = useSelector(selectNotificationOptimization);

  // Track app state changes for context
  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      setAppState(nextAppState);
    };

    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => subscription?.remove();
  }, []);

  // Record engagement with contextual data
  const recordEngagement = useCallback(
    async (
      notification: SmartNotification,
      engagementType: 'opened' | 'clicked' | 'dismissed'
    ) => {
      try {
        setError(null);

        const contextualFactors =
          notificationEngagementService.getContextualFactors();

        const engagementData: NotificationEngagementData = {
          notificationId: notification.id,
          userId,
          engagementType,
          timestamp: new Date().toISOString(),
          context: {
            ...contextualFactors,
            appState:
              appState === 'unknown'
                ? 'inactive'
                : (appState as 'active' | 'background' | 'inactive'),
          },
          metadata: {
            personalizationFactors: notification.personalizationFactors,
            aiReasoning: notification.aiReasoning,
            notificationType: notification.type,
            notificationPriority: notification.priority,
            aiConfidence: notification.confidence,
          },
        };

        // Record in service
        await notificationEngagementService.recordEngagement(engagementData);

        // Update Redux store
        await dispatch(
          recordNotificationEngagement({
            notificationId: notification.id,
            engagementType,
            context: engagementData.context,
          })
        ).unwrap();

        // Trigger pattern learning if enough data
        if (deliveryHistory.length > 0 && deliveryHistory.length % 10 === 0) {
          await notificationEngagementService.triggerPatternLearning(userId);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to record engagement');
        logger.error('Failed to record notification engagement:', err);
      }
    },
    [dispatch, userId, appState, deliveryHistory.length]
  );

  // Submit user feedback
  const submitFeedback = useCallback(
    async (
      notificationId: string,
      feedback: Omit<
        NotificationFeedback,
        'notificationId' | 'userId' | 'timestamp'
      >
    ) => {
      try {
        setError(null);

        const feedbackData: NotificationFeedback = {
          notificationId,
          userId,
          timestamp: new Date().toISOString(),
          ...feedback,
          context: {
            ...feedback.context,
            appState,
            submissionSource: 'notification_center',
          },
        };

        await notificationEngagementService.submitFeedback(feedbackData);

        // Trigger immediate pattern update for negative feedback
        if (
          ['not_helpful', 'too_frequent', 'wrong_time', 'irrelevant'].includes(
            feedback.feedbackType
          )
        ) {
          await notificationEngagementService.triggerPatternLearning(userId);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to submit feedback');
        logger.error('Failed to submit notification feedback:', err);
      }
    },
    [userId, appState]
  );

  // Analyze user patterns
  const analyzeUserPatterns = useCallback(async () => {
    try {
      setIsAnalyzing(true);
      setError(null);

      await dispatch(fetchEngagementPatterns(userId)).unwrap();
      await notificationEngagementService.triggerPatternLearning(userId);

      setLastAnalyzed(new Date().toISOString());
    } catch (err: any) {
      setError(err.message || 'Failed to analyze patterns');
      logger.error('Failed to analyze user patterns:', err);
    } finally {
      setIsAnalyzing(false);
    }
  }, [dispatch, userId]);

  // Trigger AI optimization
  const triggerOptimization = useCallback(async () => {
    try {
      setIsOptimizing(true);
      setError(null);

      await dispatch(optimizeNotificationTiming(userId)).unwrap();

      // Record optimization result
      const beforeRate = getEngagementMetrics().engagementRate;

      // Wait a bit and check improvement (in real app, this would be done over time)
      setTimeout(async () => {
        try {
          const afterRate = getEngagementMetrics().engagementRate;
          const improvement = afterRate - beforeRate;

          await notificationEngagementService.recordOptimizationResult(
            userId,
            `optimization_${Date.now()}`,
            {
              beforeEngagementRate: beforeRate,
              afterEngagementRate: afterRate,
              improvement,
              confidence: optimization?.adaptationRate || 0.5,
              factors: ['timing_optimization', 'pattern_analysis'],
            }
          );
        } catch (err) {
          logger.error('Failed to record optimization result:', err);
        }
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to optimize notifications');
      logger.error('Failed to trigger optimization:', err);
    } finally {
      setIsOptimizing(false);
    }
  }, [dispatch, userId, optimization]);

  // Calculate engagement metrics
  const getEngagementMetrics = useCallback(() => {
    if (deliveryHistory.length === 0) {
      return {
        totalNotifications: 0,
        engagementRate: 0,
        clickThroughRate: 0,
        dismissalRate: 0,
      };
    }

    const totalNotifications = deliveryHistory.length;
    const engagedCount = deliveryHistory.filter(
      delivery => delivery.openedAt || delivery.clickedAt
    ).length;
    const clickedCount = deliveryHistory.filter(
      delivery => delivery.clickedAt
    ).length;
    const dismissedCount = deliveryHistory.filter(
      delivery => delivery.dismissed
    ).length;

    return {
      totalNotifications,
      engagementRate: engagedCount / totalNotifications,
      clickThroughRate: clickedCount / totalNotifications,
      dismissalRate: dismissedCount / totalNotifications,
    };
  }, [deliveryHistory]);

  // Generate timing recommendations
  const getTimingRecommendations = useCallback(() => {
    if (!optimization || engagementPatterns.length === 0) {
      return {
        recommendations: [
          'Collect more data to provide personalized recommendations',
        ],
        confidence: 0.1,
        reasoning: ['Insufficient engagement data'],
      };
    }

    // Analyze timing effectiveness
    const timingAnalysis =
      notificationEngagementService.analyzeTimingEffectiveness(deliveryHistory);

    const recommendations: string[] = [];
    const reasoning: string[] = [];
    let confidence = 0.5;

    // Best times recommendations
    if (timingAnalysis.bestHours.length > 0) {
      const bestHoursFormatted = timingAnalysis.bestHours
        .map(hour => {
          const period = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
          return `${displayHour}${period}`;
        })
        .join(', ');

      recommendations.push(`Send notifications at ${bestHoursFormatted}`);
      reasoning.push(
        `These times show ${Math.round(timingAnalysis.overallEngagementRate * 100)}% engagement rate`
      );
      confidence += 0.2;
    }

    // Frequency recommendations
    const currentFrequency = optimization.frequencyLimit;
    const engagementRate = timingAnalysis.overallEngagementRate;

    if (engagementRate < 0.3 && currentFrequency > 3) {
      recommendations.push(
        'Reduce notification frequency to improve engagement'
      );
      reasoning.push(
        'Lower frequency may increase individual notification value'
      );
      confidence += 0.1;
    } else if (engagementRate > 0.7 && currentFrequency < 8) {
      recommendations.push('You can handle more frequent notifications');
      reasoning.push('High engagement rate suggests appetite for more content');
      confidence += 0.1;
    }

    // Pattern-based recommendations
    const timeBasedPatterns = engagementPatterns.filter(
      pattern => pattern.patternType === 'time_based' && pattern.strength > 0.6
    );

    if (timeBasedPatterns.length > 0) {
      recommendations.push(
        'AI has identified strong timing patterns in your behavior'
      );
      reasoning.push('Consistent engagement patterns detected');
      confidence += 0.15;
    }

    return {
      recommendations,
      confidence: Math.min(confidence, 1.0),
      reasoning,
    };
  }, [optimization, engagementPatterns, deliveryHistory]);

  // Auto-analyze patterns periodically
  useEffect(() => {
    if (
      userId &&
      deliveryHistory.length > 0 &&
      deliveryHistory.length % 20 === 0
    ) {
      analyzeUserPatterns();
    }
  }, [userId, deliveryHistory.length, analyzeUserPatterns]);

  return {
    recordEngagement,
    submitFeedback,
    analyzeUserPatterns,
    triggerOptimization,
    getEngagementMetrics,
    getTimingRecommendations,
    isAnalyzing,
    isOptimizing,
    lastAnalyzed,
    error,
  };
};
